import type { AxisSelection } from '@atlas/types'
import { AxisKind, ValueType } from '@atlas/types'
import { useQueries } from '@tanstack/react-query'
import { useAxes } from '@/hooks/useClassification'
import { useFacetedOptions } from '@/hooks/useLibrary'
import { type AxisValueDTO, classificationApi } from '@/services/classification.service'
import {
	AXIS_DISPLAY,
	FACETED_AXES,
	IN_SCOPE_ATTRIBUTE_VALUE_TYPES,
	isAttributeAxisAllowed,
	isEntityBackedAxis,
} from './classify-config'
import type { AxisOption, PanelAxis } from './types'
import { useCampaignOptions } from './useCampaignOptions'

/** Active values → options keyed by CODE (classify takes codes), indented by depth. */
function toOptions(values: AxisValueDTO[]): AxisOption[] {
	const active = values.filter((v) => v.validTo === null && v.status === 'active')
	const byId = new Map(active.map((v) => [v.valueId, v]))
	const depth = (v: AxisValueDTO): number => {
		let d = 0
		let cur: AxisValueDTO | undefined = v
		while (cur?.parentId && byId.has(cur.parentId)) {
			d++
			cur = byId.get(cur.parentId)
		}
		return d
	}
	return active.map((v) => ({ value: v.code, label: v.label, depth: depth(v) }))
}

/**
 * The picker overrides for an entity-backed axis (see
 * {@link ENTITY_BACKED_ATTRIBUTE_AXES}) — its options come from that entity's own
 * paged API rather than from `axis_value`, so the panel renders a picker whatever
 * the axis's `value_type` says. Every other axis gets nothing and stays driven by
 * `kind`/`value_type` alone.
 */
function entityBackedOverride(
	axisCode: string,
	source: ReturnType<typeof useCampaignOptions>,
): Partial<PanelAxis> | undefined {
	if (!isEntityBackedAxis(axisCode)) return undefined
	return {
		forcePicker: true,
		hasMore: source.hasMore,
		onLoadMore: source.loadMore,
		isLoadingMore: source.isLoadingMore,
	}
}

/**
 * Resolves the pickable dimension axes (kind=dimension, list-valued, active) and
 * their values into `PanelAxis[]` for the batch panel and row pickers. Nothing
 * hard-codes the axis set — it comes from the registry; `AXIS_DISPLAY` only
 * overrides label + order for the mockup's axes.
 *
 * `selection` cross-filters ONLY the axes in `FACETED_AXES` — today just brand,
 * narrowed by the chosen geography — via the same `useFacetedOptions` primitive
 * the Library rail, the Rules page and the new-work-order form use. That is
 * *usage-derived* — "brands with classified assets there" — not a licensing
 * statement; `axis_value_availability` exists in the schema for the declarative
 * version but is neither seeded nor read today.
 *
 * Every other axis keeps its full registry list, because usage-derived
 * narrowing is backwards in a classification form: it would hide the value
 * needed to file the first asset of a given combination.
 *
 * An empty selection costs nothing: `useFacetedOptions` serves the cached
 * bootstrap with zero facet calls until something is chosen.
 */
export function usePanelAxes(selection: AxisSelection = {}): { axes: PanelAxis[]; isLoading: boolean } {
	const { data: allAxes = [], isLoading: axesLoading } = useAxes()
	// A dimension axis is admitted exactly as before (list-valued). An attribute
	// axis has to clear TWO gates: its value_type must be one this page knows how
	// to render (`text`, `list`, `number` — `url`/`date` are excluded here, not
	// with a second filter at every downstream call site), AND its code must pass
	// the configured policy. The value-type gate alone is not an axis gate:
	// without the policy, every seeded attribute is offered, `order_code`
	// included, and typing one there attaches the file to that work order.
	const axes_ = allAxes.filter(
		(a) =>
			a.status === 'active' &&
			(a.kind === AxisKind.DIMENSION
				? a.valueType === ValueType.LIST
				: IN_SCOPE_ATTRIBUTE_VALUE_TYPES.includes(a.valueType) && isAttributeAxisAllowed(a.code)),
	)
	// Values are only ever fetched for list-valued axes — a text/number attribute
	// has no `axis_value` rows to query and never reads `options`.
	const listAxes = axes_.filter((a) => a.valueType === ValueType.LIST)
	const valueQueries = useQueries({
		queries: listAxes.map((a) => ({
			queryKey: ['values', a.axisId],
			queryFn: () => classificationApi.listValues(a.axisId),
		})),
	})
	const valuesByAxisId = new Map(listAxes.map((a, i) => [a.axisId, valueQueries[i]?.data ?? []]))

	// Only the axes in FACETED_AXES are narrowed; everything else keeps its full
	// registry list. See that constant for why.
	const axisCodes = listAxes.map((a) => a.code).filter((code) => FACETED_AXES.includes(code))
	// Attributes never participate in facet-derived filtering — an attribute
	// value in the caller's selection (e.g. `campaign`) must not become a filter
	// the server narrows `brand` by. Which codes are attributes is only knowable
	// once the registry has resolved, so until then NO selection is passed
	// through at all: filtering by a still-empty `attributeCodes` would let an
	// attribute code slip through as if it were a dimension for this render,
	// and `useFacetedOptions` debounces internally — that stale unfiltered
	// value would otherwise be the seed it eventually fires a request with.
	const attributeCodes = new Set(allAxes.filter((a) => a.kind === AxisKind.ATTRIBUTE).map((a) => a.code))
	const dimensionSelection = axesLoading
		? {}
		: Object.fromEntries(Object.entries(selection).filter(([code]) => !attributeCodes.has(code)))
	// `false`, like the new-work-order form: this is a precise tuple, not an
	// exploratory rail, so a value the rest of the selection narrows out must be
	// visibly gone rather than kept selected.
	const { optionsByAxis, isLoading: facetLoading } = useFacetedOptions(dimensionSelection, axisCodes, {
		keepNarrowedOutSelectionVisible: false,
	})
	// Only fetch the entity list when its axis is actually on offer — the attribute
	// policy can exclude it, and the page should not pay for a list it won't render.
	const campaigns = useCampaignOptions(axes_.some((a) => isEntityBackedAxis(a.code)))

	const axes: PanelAxis[] = axes_
		.map((a) => ({
			code: a.code,
			label: AXIS_DISPLAY[a.code]?.label ?? a.name,
			kind: a.kind,
			valueType: a.valueType,
			...entityBackedOverride(a.code, campaigns),
			// A faceted axis takes its narrowed options once a selection exists; the
			// registry list before then, so the first pick is made from everything.
			// Falls back on an EMPTY faceted result too — that means the bootstrap
			// has not resolved, not that the axis genuinely has no values, and
			// blanking the picker would strand the user. Unfaceted axes never appear
			// in `optionsByAxis` and so always take the registry list. The facet
			// result keys values as `code`, this panel as `value` — same string,
			// different name. A non-list axis has no values to resolve at all.
			options: isEntityBackedAxis(a.code)
				? campaigns.options
				: a.valueType !== ValueType.LIST
					? []
					: optionsByAxis[a.code]?.length
						? optionsByAxis[a.code].map((v) => ({ value: v.code, label: v.label, depth: v.depth ?? 0 }))
						: toOptions(valuesByAxisId.get(a.axisId) ?? []),
		}))
		.sort((x, y) => (AXIS_DISPLAY[x.code]?.order ?? 99) - (AXIS_DISPLAY[y.code]?.order ?? 99))
	return { axes, isLoading: axesLoading || valueQueries.some((q) => q.isLoading) || facetLoading }
}
